import { buttonVariants, cn } from '@napaicp/ui'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CompetitionCard } from '@/components/competitions/competition-card'
import { usePublicCompetitions } from '@/features/public/api'

const PREVIEW_COUNT = 3

/**
 * The lowest-priority section on `/home` (prompt §6): a small, filter-free
 * taste of what's publicly open, not a second copy of the full explorer.
 * The full search/filter/sort experience stays on `PublishedCompetitionsSection`
 * (used by `/` and unchanged by this task) — this links out to it via the
 * same `/#published-competitions` anchor the "競賽" nav item already uses.
 */
export function DiscoveryPreviewSection() {
  const { t } = useTranslation('home')
  const query = usePublicCompetitions({ limit: PREVIEW_COUNT, sort: 'newest' })
  const competitions = query.data?.competitions ?? []

  if (!query.isPending && competitions.length === 0) {
    return null
  }

  return (
    <section className="flex flex-col gap-3 pt-2" aria-labelledby="discovery-preview-heading">
      <div className="flex items-center justify-between gap-2">
        <h2 id="discovery-preview-heading" className="text-lg font-semibold tracking-tight">
          {t('discoveryPreview.title')}
        </h2>
        <Link
          className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }))}
          to="/#published-competitions"
        >
          {t('discoveryPreview.exploreAll')}
        </Link>
      </div>
      {query.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              aria-hidden="true"
              className="h-32 animate-pulse rounded-xl border border-border bg-muted"
            />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {competitions.map((competition) => (
            <CompetitionCard
              key={competition.slug}
              href={`/c/${competition.slug}`}
              status={competition.status}
              name={competition.name}
              summary={competition.shortDescription}
              ctaLabel={t('discoveryPreview.viewDetails')}
              t={t}
            />
          ))}
        </div>
      )}
    </section>
  )
}
